import { apiJson, apiMultipart } from "../utils/api.js";
import { handleApiError } from "../utils/errorHandler.js";
import { SpiceLevelTranslations } from "../constants/index.js";

/**
 * Get all menus/dishes
 * @param {string} chefId - Chef ID
 * @param {number} offset - Pagination offset
 * @param {number} limit - Pagination limit (default 20)
 * @param {Object} [options] - Optional query modifiers
 * @param {string} [options.status] - Filter by dish status (e.g. ACTIVE)
 * @returns {Promise<Object>} Menu data
 */
export const getAllMenus = async (chefId, offset = 0, limit = 20, options = {}) => {
    try {
        const params = { offset, limit };
        if (options.status) {
            params.status = options.status;
        }
        const response = await apiJson.get(`/kms/dish/all`, {
            params
        });
        return response.data;
    } catch (error) {
        handleApiError(error, {
            defaultMessage: "Error: 1001: Ոչ մի ուտեստ չգտնվեց: Խնդրում ենք ստուգել տվյալները և փորձել կրկին, կամ դիմել աջակցության բաժին:"
        });
        throw error;
    }
};

/**
 * Create a new dish
 * @param {string} chefId - Chef ID
 * @param {Object} dishData - Dish data
 * @param {File} image - Optional dish image
 * @returns {Promise<Object>} Created dish data
 */
export const createDish = async (chefId, dishData, image) => {
    try {
        const formData = new FormData();
        formData.append("dto", JSON.stringify(dishData));

        if (image) {
            formData.append("file", image);
        }

        const response = await apiMultipart.post(`/kms/dish`, formData);
        return response.data;
    } catch (error) {
        handleApiError(error, {
            defaultMessage: "Error: 1002: Ուտեստի բաղադրիչները բացակայում են: Խնդրում ենք համոզվել, որ ուտեստի բաղադրիչները լրացված են և փորձել կրկին, կամ դիմել աջակցության բաժին"
        });
        throw error;
    }
};

/**
 * Update an existing dish
 * @param {string} dishId - Dish ID
 * @param {Object} dishData - Updated dish data
 * @param {File} imageFile - Optional new dish image
 * @returns {Promise<Object>} Updated dish data
 */
export const updateDish = async (dishId, dishData, imageFile = null) => {
    try {
        const formData = new FormData();
        formData.append("dto", JSON.stringify(dishData));

        if (imageFile) {
            formData.append("file", imageFile);
        }

        const response = await apiMultipart.put(`/kms/dish/${dishId}`, formData);
        return response.data;
    } catch (error) {
        handleApiError(error, {
            defaultMessage: "Error: 1003: Ուտեստի տվյալների թարմացումը ձախողվեց սխալ մուտքագրված տվյալների պատճառով: Խնդրում ենք ստուգել մուտքագրված տվյալները և փորձել կրկին, կամ դիմել աջակցության բաժին:"
        });
        throw error;
    }
};

/**
 * Update dish status
 * @param {string} dishId - Dish ID
 * @param {string} status - New status (PENDING, ACTIVE, DISABLED, DELETED, REJECTED)
 * @returns {Promise<Object>} Updated dish data
 */
export const updateDishStatus = async (dishId, status) => {
    try {
        // Backend uses query param only, not multipart/form-data
        const response = await apiJson.put(`/kms/dish/${dishId}/update-status`, null, {
            params: { status }
        });
        return response.data;
    } catch (error) {
        handleApiError(error, {
            defaultMessage: "Error: 1004: Ուտեստի նոր կարգավիճակը անվավեր է: Խնդրում ենք վերանայել մուտքագրված տվյալները և փորձել կրկին, կամ դիմել աջակցության բաժին"
        });
        throw error;
    }
};

/**
 * Delete a dish
 * @param {string} dishId - Dish ID
 * @returns {Promise<void>}
 */
export const deleteDish = async (dishId) => {
    try {
        await apiMultipart.delete(`/kms/dish/${dishId}`);
    } catch (error) {
        handleApiError(error, {
            defaultMessage: "Error: 1004: Ուտեստի նոր կարգավիճակը անվավեր է: Խնդրում ենք վերանայել մուտքագրված տվյալները և փորձել կրկին, կամ դիմել աջակցության բաժին"
        });
        throw error;
    }
};

/**
 * Get dish by ID
 * @param {string} id - Dish ID
 * @returns {Promise<Object>} Dish data
 */
export const getDishById = async (id) => {
    try {
        const response = await apiJson.get(`/kms/dish/${id}`);
        return response.data;
    } catch (error) {
        handleApiError(error, {
            defaultMessage: "Error: 1005: Ուտեստը չի գտնվել: Խնդրում ենք ստուգել տվյալները և փորձել կրկին, կամ դիմել աջակցության բաժին"
        });
        throw error;
    }
};

/**
 * Fetch all dish tags
 * @returns {Promise<Array>} Array of dish tags
 */
export const fetchDishTags = async () => {
    try {
        const response = await apiJson.get(`/data/dish-tags`);
        return response.data;
    } catch (error) {
        handleApiError(error, {
            defaultMessage: "Error: 1006: Տեղի է ունեցել սխալ, խնդրում ենք փորձել մի փոքր ուշ"
        });
        throw error;
    }
};
/**
 * Fetch all dish cuisine tags
 * @returns {Promise<Array>} Array of cuisine tags
 */
export const fetchDishCuisineTags = async () => {
    try {
        const response = await apiJson.get(`/data/dish-cuisine-tags`);
        return response.data;
    } catch (error) {
        handleApiError(error, {
            defaultMessage: "Error: 1007: Տեղի է ունեցել սխալ, խնդրում ենք փորձել մի փոքր ուշ"
        });
        throw error;
    }
};

/**
 * Fetch adjustable spice levels
 * @returns {Promise<Array>} Array of spice levels with translations
 */
export const fetchAdjustableSpiceLevels = async () => {
    try {
        const response = await apiJson.get(`/data/dish-adjustable-spice-levels`);
        return response.data.map((item) => ({
            ...item,
            spiceLevelApiKey: item.spiceLevel,
            spiceLevel: SpiceLevelTranslations[item.spiceLevel] || item.spiceLevel,
        }));
    } catch (error) {
        handleApiError(error, {
            defaultMessage: "Error: 1008: Կծվության մակարդակը անվավեր է: Խնդրում ենք ընտրել ճիշտ տարբերակը և փորձել կրկին, կամ դիմել աջակցության բաժին:"
        });
        throw error;
    }
};

/**
 * Fetch dish dietary options
 * @returns {Promise<Array>} Array of dietary options
 */
export const fetchDishDietaryOptions = async () => {
    try {
        const response = await apiJson.get(`/data/dish-dietary-options`);
        return response.data;
    } catch (error) {
        handleApiError(error, {
            defaultMessage: "Error: 1009: Տեղի է ունեցել սխալ, խնդրում ենք փորձել մի փոքր ուշ"
        });
        throw error;
    }
};

/**
 * Fetch dish types
 * @returns {Promise<Array>} Array of dish types
 */
export const fetchDishTypes = async () => {
    try {
        const response = await apiJson.get(`/data/dish-types`);
        return response.data;
    } catch (error) {
        handleApiError(error, {
            defaultMessage: "Error: 1010: Տեղի է ունեցել սխալ, խնդրում ենք փորձել մի փոքր ուշ"
        });
        throw error;
    }
};

/**
 * Fetch dish additions
 * @returns {Promise<Array>} Array of dish additions
 */
export const fetchDishAdditions = async () => {
    try {
        const response = await apiJson.get(`/kms/dish/addition`);
        return response.data;
    } catch (error) {
        handleApiError(error, {
            defaultMessage: "Error: 1011: Տեղի է ունեցել սխալ, խնդրում ենք փորձել մի փոքր ուշ"
        });
        return [];
    }
};

/**
 * Fetch addition groups
 * @returns {Promise<Array>} Array of addition groups
 */
export const fetchAdditionGroups = async () => {
    try {
        const response = await apiJson.get(`/kms/additions-group`);
        return response.data;
    } catch (error) {
        handleApiError(error, {
            defaultMessage: "Error: 1015: Հավելումների խմբերը բեռնելու սխալ։"
        });
        return [];
    }
};
/**
 * Save a new dish addition
 * @param {Object} addition - Addition data
 * @returns {Promise<void>}
 */
export const saveDishAddition = async (addition) => {
    try {
        const payload = {
            nameHy: addition.nameHy,
            price: Number(addition.price),
            nameEn: addition.nameEn || '',
            nameRu: addition.nameRu || '',
            group: addition.group || null,
        };
        await apiJson.post(`/kms/dish/addition`, payload);
    } catch (error) {
        handleApiError(error, {
            defaultMessage: "Error: 1012: Հավելումը պահպանելու սխալ։"
        });
        throw error;
    }
};
/**
 * Update an existing dish addition
 * @param {Object} addition - Updated addition data
 * @returns {Promise<void>}
 */
export const updateDishAddition = async (addition) => {
    try {
        const payload = {
            id: addition.id,
            nameHy: addition.nameHy,
            price: Number(addition.price),
            nameEn: addition.nameEn || '',
            nameRu: addition.nameRu || '',
            group: addition.group || null,
        };
        await apiJson.put(`/kms/dish/addition/${addition.id}`, payload);
    } catch (error) {
        handleApiError(error, {
            defaultMessage: "Error: 1013: Հավելումը թարմացնելու սխալ։"
        });
        throw error;
    }
};
/**
 * Delete a dish addition
 * @param {string} id - Addition ID
 * @returns {Promise<void>}
 */
export const deleteDishAddition = async (id) => {
    try {
        await apiJson.delete(`/kms/dish/addition/${id}`);
    } catch (error) {
        handleApiError(error, {
            defaultMessage: "Error: 1014: Տեղի է ունեցել սխալ, խնդրում ենք փորձել մի փոքր ուշ"
        });
        throw error;
    }
};

/**
 * Fetch dish tag sorting with dishes grouped by tags
 * @param {string} chefId - Chef ID
 * @returns {Promise<Array>} Array of tag data with dishes
 */
export const fetchDishTagSorting = async (chefId) => {
    try {
        // Fetch the dish tag sorting data
        const sortingResponse = await apiJson.get(`/kms/dish-tag-sorting`);

        // Fetch all dish tags
        const dishTagsResponse = await fetchDishTags();

        // Fetch all dishes
        const dishesResponse = await getAllMenus(chefId, 0, 1000); // Fetch a large number to get all dishes

        if (!sortingResponse.data || !dishTagsResponse || !dishesResponse?.dishDtoTreeSet) {
            throw new Error("Failed to fetch required data");
        }

        const sortingData = sortingResponse.data;
        const dishTags = dishTagsResponse;
        const dishes = dishesResponse.dishDtoTreeSet;

        // Create a map of dishTagId to priorityIndex
        const priorityMap = {};
        sortingData.forEach(item => {
            priorityMap[item.dishTagId] = item.priorityIndex;
        });

        // Group dishes by tag
        const dishesByTag = {};
        dishes.forEach(dish => {
            if (dish.dishTagDto) {
                const tagId = dish.dishTagDto.id;
                if (!dishesByTag[tagId]) {
                    dishesByTag[tagId] = [];
                }
                dishesByTag[tagId].push(dish);
            }
        });

        // Create the tag data structure expected by the ordering.js component
        const tagData = dishTags
            .filter(tag => priorityMap[tag.id] !== undefined) // Only include tags that have a priority
            .map(tag => ({
                id: tag.id,
                tag: tag.tag,
                translations: tag.translations,
                dishes: dishesByTag[tag.id] || []
            }))
            .sort((a, b) => priorityMap[a.id] - priorityMap[b.id]); // Sort by priorityIndex

        return tagData;
    } catch (error) {
        handleApiError(error, {
            defaultMessage: "Error: 1016: Տեղի է ունեցել սխալ, խնդրում ենք փորձել մի փոքր ուշ"
        });
        return [];
    }
};

/**
 * Update dish tag sorting order
 * @param {string} chefId - Chef ID
 * @param {Array<number>} orderedChefTagIds - Ordered array of tag IDs
 * @returns {Promise<boolean>} Success status
 */
export const updateDishTagSorting = async (chefId, orderedChefTagIds) => {
    try {
        const payload = {
            chefId: Number(chefId),
            orderedChefTagIds
        };

        await apiJson.put(`/kms/dish-tag-sorting`, payload);
        return true;
    } catch (error) {
        handleApiError(error, {
            defaultMessage: "Error: 1017: Տեղի է ունեցել սխալ, խնդրում ենք փորձել մի փոքր ուշ"
        });
        return false;
    }
};

/**
 * Update dish sorting order within a tag
 * @param {string} chefId - Chef ID
 * @param {string} tagId - Tag ID
 * @param {Array<number>} orderedDishIds - Ordered array of dish IDs
 * @returns {Promise<boolean>} Success status
 */
export const updateDishSorting = async (chefId, tagId, orderedDishIds) => {
    try {
        const payload = {
            chefId: Number(chefId),
            tagId: Number(tagId),
            orderedDishIds
        };

        await apiJson.put(`/kms/dish-sorting`, payload);
        return true;
    } catch (error) {
        // Don't show toast here - let the component handle it
        handleApiError(error, {
            defaultMessage: "Error: 1018: Տեղի է ունեցել սխալ, խնդրում ենք փորձել կրկին, կամ դիմել աջակցության բաժին",
            showToast: false
        });
        return false;
    }
};

/**
 * Export all dishes to Excel
 * Uses axios (apiJson) to leverage auth interceptors and consistent baseURL
 * @returns {Promise<Blob>} Excel file blob
 */
export const exportDishesToExcel = async () => {
    try {
        const response = await apiJson.post(
            `/kms/dish/export`,
            null,
            { responseType: "blob" }
        );

        // Check if response is actually an error (JSON instead of blob)
        const contentType = response.headers["content-type"];
        if (contentType && contentType.includes("application/json")) {
            const text = await response.data.text();
            let errorMessage = "Failed to export dishes";
            try {
                const json = JSON.parse(text);
                errorMessage = json.message || errorMessage;
            } catch {
                errorMessage = text || errorMessage;
            }
            throw new Error(errorMessage);
        }

        return response.data;
    } catch (error) {
        // Handle axios errors with blob responses
        if (error.response?.data instanceof Blob) {
            const contentType = error.response.headers?.["content-type"];
            if (contentType && contentType.includes("application/json")) {
                try {
                    const text = await error.response.data.text();
                    const json = JSON.parse(text);
                    throw new Error(json.message || text || "Failed to export dishes");
                } catch (parseError) {
                    // If parsing fails, use original error
                }
            }
        }

        // Re-throw with user-friendly message if available
        if (error.message) {
            throw error;
        }
        throw new Error(error.response?.data?.message || "Failed to export dishes");
    }
};

/**
 * Import dishes from CSV/Excel file
 * @param {File} file - CSV or Excel file
 * @param {File} imageZipFile - Optional ZIP file containing images
 * @param {Function} onProgress - Optional callback for upload progress (progress: 0-100)
 * @param {AbortSignal} signal - Optional AbortSignal to cancel the request
 * @returns {Promise<Object>} Import result with success/error counts
 */
export const importDishesFromFile = async (file, imageZipFile = null, onProgress = null, signal = null) => {
    try {
        const formData = new FormData();
        formData.append("file", file);
        
        if (imageZipFile) {
            formData.append("imageZip", imageZipFile);
        }

        const config = {};
        
        // Add abort signal if provided
        if (signal) {
            config.signal = signal;
        }
        
        // Add progress tracking if callback provided
        if (onProgress) {
            config.onUploadProgress = (progressEvent) => {
                if (progressEvent.total) {
                    const percentCompleted = Math.round(
                        (progressEvent.loaded * 100) / progressEvent.total
                    );
                    onProgress(percentCompleted);
                }
            };
        }

        const response = await apiMultipart.post(`/kms/dish/import`, formData, config);
        return response.data;
    } catch (error) {
        // Don't show error if request was cancelled
        if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
            throw new Error('Import cancelled');
        }
        
        // Extract error message from backend ResponseError format
        let errorMessage = "Error: 1019: Menu import failed. Please check your file and try again.";
        if (error.response?.data) {
            // Backend returns ResponseError with {code, message, developerMessage}
            if (error.response.data.message) {
                errorMessage = error.response.data.message;
            } else if (error.response.data.developerMessage) {
                errorMessage = error.response.data.developerMessage;
            } else if (typeof error.response.data === 'string') {
                errorMessage = error.response.data;
            }
        } else if (error.message) {
            errorMessage = error.message;
        }
        
        // Don't show toast here - let the component handle it
        handleApiError(error, {
            defaultMessage: errorMessage,
            showToast: false
        });
        
        // Create error with proper message
        const importError = new Error(errorMessage);
        importError.response = error.response;
        throw importError;
    }
};

/**
 * Download import template file
 * @returns {Promise<Blob>} Excel template file blob
 */
export const downloadImportTemplate = async () => {
    try {
        const response = await apiJson.get(
            `/kms/dish/import-template`,
            { responseType: "blob" }
        );

        // Check if response is actually an error (JSON instead of blob)
        const contentType = response.headers["content-type"];
        if (contentType && contentType.includes("application/json")) {
            const text = await response.data.text();
            let errorMessage = "Failed to download template";
            try {
                const json = JSON.parse(text);
                errorMessage = json.message || errorMessage;
            } catch {
                errorMessage = text || errorMessage;
            }
            throw new Error(errorMessage);
        }

        return response.data;
    } catch (error) {
        // Handle axios errors with blob responses
        if (error.response?.data instanceof Blob) {
            const contentType = error.response.headers?.["content-type"];
            if (contentType && contentType.includes("application/json")) {
                try {
                    const text = await error.response.data.text();
                    const json = JSON.parse(text);
                    // Backend returns ResponseError format: {code, message, developerMessage}
                    const errorMessage = json.message || json.developerMessage || text || "Failed to download template";
                    throw new Error(errorMessage);
                } catch (parseError) {
                    // If parsing fails, use original error
                }
            }
        }

        // Extract error message from backend ResponseError format
        let errorMessage = "Failed to download template";
        if (error.response?.data) {
            if (error.response.data.message) {
                errorMessage = error.response.data.message;
            } else if (error.response.data.developerMessage) {
                errorMessage = error.response.data.developerMessage;
            } else if (typeof error.response.data === 'string') {
                errorMessage = error.response.data;
            }
        } else if (error.message) {
            errorMessage = error.message;
        }
        
        throw new Error(errorMessage);
    }
};

// Addition group CRUD helpers
export const createAdditionGroup = async (groupData) => {
    try {
        const payload = {
            nameHy: groupData.nameHy,
            nameRu: groupData.nameRu,
            nameEn: groupData.nameEn,
            isMulti: !!groupData.isMulti,
            isRequired: !!groupData.isRequired
        };
        const response = await apiJson.post(`/kms/additions-group`, payload);
        return response.data;
    } catch (error) {
        handleApiError(error, {
            defaultMessage: "Error: 1015: Չհաջողվեց ստեղծել հավելումների խումբ։"
        });
        throw error;
    }
};

export const updateAdditionGroup = async (groupId, groupData) => {
    try {
        const payload = {
            nameHy: groupData.nameHy,
            nameRu: groupData.nameRu,
            nameEn: groupData.nameEn,
            isMulti: !!groupData.isMulti,
            isRequired: !!groupData.isRequired
        };
        const response = await apiJson.put(`/kms/additions-group/${groupId}`, payload);
        return response.data;
    } catch (error) {
        handleApiError(error, {
            defaultMessage: "Error: 1015: Չհաջողվեց թարմացնել հավելումների խումբը։"
        });
        throw error;
    }
};

export const deleteAdditionGroup = async (groupId) => {
    try {
        await apiJson.delete(`/kms/additions-group/${groupId}`);
    } catch (error) {
        const isUsed = error?.response?.data?.code === "used-addition-group-id";
        handleApiError(error, {
            defaultMessage: isUsed
                ? "Cannot delete group because it has associated additions. Please remove or reassign additions first."
                : "Error: 1015: Խմբի հեռացումը ձախողվեց։"
        });
        throw error;
    }
};
